import alea from 'alea';

import type { DeckHistory, DeckHistoryEvent } from './deck-page.ts';

export type HistoryMark = {
  cardId: string;
  dayKey: string;
  event: DeckHistoryEvent;
  pileIndex: number;
  rotation: number;
};

export type HistoryLayout = {
  dayKeys: string[];
  height: number;
  marks: HistoryMark[];
  width: number;
};

export type HistoryTick = {
  label: string;
  value: number;
};

const DAY_WIDTH = 96;
const MARK_WIDTH = 24;
const MARK_HEIGHT = 14;
const MARK_RADIUS = 4;
const MARK_STROKE_WIDTH = 1;
const MARK_OVERLAP = 2;
const PILE_STEP = MARK_HEIGHT - MARK_OVERLAP;
const ROTATION_CLEARANCE = 4;
const MARK_AXIS_GAP = 10;
const AXIS_HEIGHT = 30;
const TOP_PADDING = 8;
const MIN_HEIGHT = 128;
const MAX_VISIBLE_TICKS = 7;

/** A card keeps one physical identity wherever it appears in a visualization. */
export function stableCardRotation(cardId: string): number {
  const random = alea(cardId);
  return Math.round((random() * 8 - 4) * 10) / 10;
}

export function historyMarkClassName(
  result: DeckHistoryEvent['result'],
): string {
  return result === 'correct'
    ? 'fill-success-surface stroke-success'
    : 'fill-error-surface stroke-destructive';
}

function localDayKey(timestamp: number): string {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dayKeyDate(dayKey: string): Date {
  const [year, month, day] = dayKey.split('-').map(Number);
  return new Date(year!, month! - 1, day!);
}

function dayKeysBetween(first: string, last: string): string[] {
  const date = dayKeyDate(first);
  const end = dayKeyDate(last);
  const keys: string[] = [];
  while (date <= end) {
    keys.push(localDayKey(date.getTime()));
    date.setDate(date.getDate() + 1);
  }
  return keys;
}

export function layoutHistory(history: DeckHistory): HistoryLayout {
  const grouped = new Map<string, DeckHistoryEvent[]>();
  for (const event of history.events) {
    const key = localDayKey(event.occurredAt);
    const events = grouped.get(key) ?? [];
    events.push(event);
    grouped.set(key, events);
  }
  const eventDayKeys = [...grouped.keys()].sort();
  const dayKeys =
    eventDayKeys.length > 0
      ? dayKeysBetween(eventDayKeys[0]!, eventDayKeys.at(-1)!)
      : [];
  const normalized = [...history.events]
    .sort((a, b) => a.occurredAt - b.occurredAt || a.id.localeCompare(b.id))
    .map((event) => {
      const dayKey = localDayKey(event.occurredAt);
      return {
        cardId: event.cardId,
        dayKey,
        event,
        pileIndex: 0,
        rotation: stableCardRotation(event.cardId),
      } satisfies HistoryMark;
    });
  const marksByDay = new Map<string, HistoryMark[]>();
  for (const mark of normalized) {
    const dayMarks = marksByDay.get(mark.dayKey) ?? [];
    dayMarks.push(mark);
    marksByDay.set(mark.dayKey, dayMarks);
  }
  const marks = dayKeys.flatMap((dayKey) => {
    const dayMarks = marksByDay.get(dayKey) ?? [];
    return dayMarks.map((mark, pileIndex) => ({ ...mark, pileIndex }));
  });
  const largestPile = Math.max(
    ...[...marksByDay.values()].map((dayMarks) => dayMarks.length),
    0,
  );
  return {
    dayKeys,
    height: Math.max(
      MIN_HEIGHT,
      TOP_PADDING +
        MARK_HEIGHT +
        (largestPile - 1) * PILE_STEP +
        MARK_AXIS_GAP +
        AXIS_HEIGHT +
        ROTATION_CLEARANCE,
    ),
    marks,
    width: dayKeys.length * DAY_WIDTH,
  };
}

export function markPosition(
  mark: HistoryMark,
  layout: Pick<HistoryLayout, 'dayKeys' | 'height' | 'width'>,
): { x: number; y: number } {
  const index = layout.dayKeys.indexOf(mark.dayKey);
  return {
    x: index * DAY_WIDTH + DAY_WIDTH / 2,
    y:
      layout.height -
      AXIS_HEIGHT -
      ROTATION_CLEARANCE -
      MARK_AXIS_GAP -
      MARK_HEIGHT -
      mark.pileIndex * PILE_STEP,
  };
}

export function historyTicks(
  layout: Pick<HistoryLayout, 'dayKeys' | 'width'>,
): HistoryTick[] {
  const count = layout.dayKeys.length;
  if (count === 0) return [];
  const visibleCount = Math.min(MAX_VISIBLE_TICKS, count);
  const indexes = new Set(
    Array.from({ length: visibleCount }, (_, index) =>
      Math.round((index * (count - 1)) / Math.max(visibleCount - 1, 1)),
    ),
  );
  return [...indexes].map((index) => ({
    label: layout.dayKeys[index]!,
    value: index * DAY_WIDTH + DAY_WIDTH / 2,
  }));
}

export const historyGeometry = {
  axisHeight: AXIS_HEIGHT,
  dayWidth: DAY_WIDTH,
  markHeight: MARK_HEIGHT,
  markAxisGap: MARK_AXIS_GAP,
  markRadius: MARK_RADIUS,
  markStrokeWidth: MARK_STROKE_WIDTH,
  markWidth: MARK_WIDTH,
  markOverlap: MARK_OVERLAP,
  pileStep: PILE_STEP,
  rotationCenterX: MARK_WIDTH / 2,
  rotationCenterY: MARK_HEIGHT / 2,
  rotationClearance: ROTATION_CLEARANCE,
  topPadding: TOP_PADDING,
};
